import { Fragment, useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import type { AuditEvent, AuditEventQuery } from '@regigov/contracts';
import { auditApi } from '../api/audit';
import { Field, ProblemAlert, StatusBadge } from './ui';

const sydneyTime = new Intl.DateTimeFormat('en-AU', { timeZone: 'Australia/Sydney', dateStyle: 'medium', timeStyle: 'medium' });

const FILTERS = ['entityId', 'actor', 'action', 'from', 'to'] as const;
type Draft = Record<(typeof FILTERS)[number], string> & { related: boolean };

const ENTITY_PATH: Record<string, (id: string) => string> = {
  compliance_case: (id) => `/cer/compliance/cases/${id}`,
  compliance_audit_follow_up: () => '/cer/compliance/follow-ups',
  monitoring_run: (id) => `/cer/compliance/monitoring/runs/${id}`,
  participant: (id) => `/cer/participants/${id}`,
  application: (id) => `/cer/participants/applications/${id}`,
  device_model: (id) => `/cer/register/models/${id}`,
  device_unit: (id) => `/cer/register/units/${id}`,
  installation: (id) => `/cer/register/installations/${id}`,
  incident: (id) => `/cer/incidents/${id}`,
  recall: (id) => `/cer/recalls/${id}`,
  outbox: (id) => `/cer/exchange/outbox/${id}`,
  dead_letter: (id) => `/cer/exchange/dead-letters/${id}`,
  reconciliation_run: (id) => `/cer/exchange/reconciliation/${id}`,
};

/** The record's page, or for an inspection, audit or enforcement action its case. */
function recordPath(e: AuditEvent): string | null {
  if (e.entityType && e.entityId && ENTITY_PATH[e.entityType]) return ENTITY_PATH[e.entityType]!(e.entityId);
  const caseId = e.detail.caseId;
  return typeof caseId === 'string' ? `/cer/compliance/cases/${caseId}` : null;
}

/** CER audit trail (compliance demo C10): `audit.events` filtered by record, actor, action and date (Sydney time), newest first. */
export function AuditTrailPage() {
  const [params, setParams] = useSearchParams();
  const current: Draft = { ...(Object.fromEntries(FILTERS.map((k) => [k, params.get(k) ?? ''])) as Record<(typeof FILTERS)[number], string>), related: params.get('related') === 'true' };
  const before = params.get('before') ?? '';
  const [draft, setDraft] = useState<Draft>(current);
  const key = params.toString();
  useEffect(() => {
    setDraft(current);
  }, [key]);

  const query: AuditEventQuery = {
    entityId: current.entityId || undefined,
    related: current.entityId && current.related ? 'true' : undefined,
    actor: current.actor || undefined,
    action: current.action || undefined,
    from: current.from || undefined,
    to: current.to || undefined,
    before: before ? Number(before) : undefined,
  };
  const events = useQuery({ queryKey: ['audit', 'events', key], queryFn: () => auditApi.events(query), staleTime: 0, gcTime: 0 });
  const actions = useQuery({ queryKey: ['audit', 'actions'], queryFn: auditApi.actions });

  const apply = (d: Draft, page?: number) => {
    const next = new URLSearchParams();
    for (const k of FILTERS) if (d[k].trim()) next.set(k, d[k].trim());
    if (d.related && d.entityId.trim()) next.set('related', 'true');
    if (page) next.set('before', String(page));
    setParams(next);
  };
  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    apply(draft);
  };
  const set = (k: (typeof FILTERS)[number]) => (e: { target: { value: string } }) => setDraft({ ...draft, [k]: e.target.value });

  const page = events.data;
  return (
    <>
      <div className="row">
        <h1>Audit trail</h1>
        <Link to="/cer/reports">Compliance activity report</Link>
        <Link to="/cer/compliance/cases">Cases</Link>
      </div>
      <p className="notice">
        Every recorded action in the sandbox, newest first: who did it, when, to which record and with what detail. Dates are calendar days in Sydney, Australia time and both ends are included. The action filter
        matches from the start, so <code>compliance.case</code> finds every case step.
      </p>
      <form onSubmit={onSubmit}>
        <div className="row">
          <Field label="Record id">
            <input value={draft.entityId} onChange={set('entityId')} placeholder="e.g. CER-C-…" style={{ width: '24rem' }} />
          </Field>
          <label>
            <input type="checkbox" checked={draft.related} disabled={!draft.entityId.trim()} onChange={(e) => setDraft({ ...draft, related: e.target.checked })} /> Include the case's inspections, audits and actions
          </label>
        </div>
        <div className="row">
          <Field label="Actor">
            <input value={draft.actor} onChange={set('actor')} placeholder="Username or name" />
          </Field>
          <Field label="Action">
            <input value={draft.action} onChange={set('action')} list="audit-actions" placeholder="e.g. compliance.case" />
          </Field>
          <datalist id="audit-actions">
            {actions.data?.items.map((a) => <option key={a} value={a} />)}
          </datalist>
          <Field label="From">
            <input type="date" value={draft.from} onChange={set('from')} />
          </Field>
          <Field label="To">
            <input type="date" value={draft.to} onChange={set('to')} />
          </Field>
          <button type="submit">Search</button>
          <button type="button" className="secondary" onClick={() => apply({ entityId: '', actor: '', action: '', from: '', to: '', related: false })}>
            Clear
          </button>
        </div>
      </form>
      <ProblemAlert error={events.error ?? actions.error} />
      {events.isLoading && <p className="muted">Loading…</p>}
      {page && (
        <>
          <AuditEventTable key={key} items={page.items} />
          <div className="row">
            {before && (
              <button type="button" className="secondary" onClick={() => apply(current)}>
                Newest
              </button>
            )}
            {page.nextBefore !== null && (
              <button type="button" className="secondary" onClick={() => apply(current, page.nextBefore!)}>
                Older events
              </button>
            )}
          </div>
        </>
      )}
    </>
  );
}

/** Audit events, newest first, each with its detail on demand. `extra` adds a column before the detail toggle. */
export function AuditEventTable<E extends AuditEvent>({ items, extra }: { items: E[]; extra?: { header: string; cell: (e: E) => ReactNode } }) {
  const [open, setOpen] = useState<number | null>(null);
  const columns = extra ? 8 : 7;
  return (
    <table className="table">
      <thead>
        <tr>
          <th>When (Sydney time)</th>
          <th>Actor</th>
          <th>Action</th>
          <th>Record</th>
          <th>Participant</th>
          <th>Outcome</th>
          {extra && <th>{extra.header}</th>}
          <th />
        </tr>
      </thead>
      <tbody>
        {items.length === 0 && (
          <tr>
            <td colSpan={columns} className="muted">
              No audit events match.
            </td>
          </tr>
        )}
        {items.map((e) => {
          const path = recordPath(e);
          return (
            <Fragment key={e.id}>
              <tr>
                <td>{sydneyTime.format(new Date(e.occurredAt))}</td>
                <td>{e.actorName ? `${e.actorName} (${e.actorUsername})` : (e.actorId ?? e.actorType)}</td>
                <td>
                  <code>{e.action}</code>
                </td>
                <td>
                  {e.entityType && <div className="muted">{e.entityType.replace(/_/g, ' ')}</div>}
                  {e.entityId && (path ? <Link to={path}>{e.entityId}</Link> : <code>{e.entityId}</code>)}
                </td>
                <td>{e.participantId ? (e.participantName ?? e.participantId) : '—'}</td>
                <td>
                  <StatusBadge status={e.outcome} />
                </td>
                {extra && <td>{extra.cell(e)}</td>}
                <td>
                  <button type="button" className="linklike" onClick={() => setOpen(open === e.id ? null : e.id)}>
                    {open === e.id ? 'Hide detail' : 'Detail'}
                  </button>
                </td>
              </tr>
              {open === e.id && (
                <tr>
                  <td colSpan={columns}>
                    <p className="muted">
                      Event #{e.id} at {e.occurredAt}
                      {e.correlationId && <> · correlation {e.correlationId}</>}
                    </p>
                    <pre>{JSON.stringify(e.detail, null, 2)}</pre>
                  </td>
                </tr>
              )}
            </Fragment>
          );
        })}
      </tbody>
    </table>
  );
}
